import Cookies from 'js-cookie';
import { IFrameHelper } from '../sdk/IFrameHelper';
import { getBubbleView } from '../sdk/bubbleHelpers';
import { WidgetHandler } from '../sdk/WidgetHandler';
import { ErrorHandler } from '../sdk/ErrorHandler';
import { WIDGET_EVENTS } from '../sdk/constants';
import md5 from 'md5';

const REQUIRED_USER_KEYS = ['avatar_url', 'email', 'name'];
const ALLOWED_USER_ATTRIBUTES = ['avatar_url', 'email', 'name'];
const WIDGET_Z_INDEX = 2147483647;
const bubbleImg = 'https://ik.imagekit.io/3lj5qznld/LC%20Icon.svg';

let widgetHandler = null;
let errorHandler = null;
let sdkConfig = {};

const isMobile = () =>
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  ) || window.innerWidth <= 768;

export const getUserCookieName = () => {
  const SET_USER_COOKIE_PREFIX = 'cw_user_';
  const { websiteToken: websiteIdentifier } = window.$chatwoot;
  return `${SET_USER_COOKIE_PREFIX}${websiteIdentifier}`;
};

export const getUserString = ({ identifier = '', user }) => {
  const userStringWithSortedKeys = ALLOWED_USER_ATTRIBUTES.reduce(
    (acc, key) => `${acc}${key}${user[key] || ''}`,
    ''
  );
  return `${userStringWithSortedKeys}identifier${identifier}`;
};

const computeHashForUserData = (...args) => md5(getUserString(...args));

export const hasUserKeys = (user) =>
  REQUIRED_USER_KEYS.reduce((acc, key) => acc || !!user[key], false);

const fetchWidgetConfig = async (baseUrl, websiteToken) => {
  try {
    const response = await fetch(
      `${baseUrl}/widget_config?website_token=${websiteToken}`
    );
    const data = await response.json();
    const defaultConfig =
      typeof data?.config === 'string' ? JSON.parse(data.config) : {};

    return {
      widgetColor: data?.widget_color,
      popup_configs: data?.popup_configs,
      popup_message_enabled: data?.popup_message_enabled || false,
      homepopupsettingOptions: data?.homepopupsettingOptions,
      closepopupsettingOptions: data?.closewidgetpopupsettingOptions,
      utm_for_widget: data?.utm_for_widget || false,
      hideWidgetIcon: data?.hide_widget_icon || false,
      disappearOnScrollingDesktop:
        defaultConfig?.disappearOnScrollingDesktop || false,
      disappearOnScrollingMobile:
        defaultConfig?.disappearOnScrollingMobile || false,
      widgetIconDesktop: defaultConfig?.widgetIconDesktop || '',
      widgetIconMobile: defaultConfig?.widgetIconMobile || '',
      default_config: defaultConfig,
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to fetch widget config:', error);
    window.dispatchEvent(
      new CustomEvent(WIDGET_EVENTS.ERROR, {
        detail: { error: error.message, action: 'fetch_config' },
      })
    );
    return { default_config: {} };
  }
};

const loadIframe = () => {
  const iframe = IFrameHelper.getAppFrame();
  if (iframe && iframe.src === '') {
    const src = iframe.getAttribute('data-src');
    if (src) {
      iframe.src = src;
    }
  }
};

const runSDK = async ({ baseUrl, websiteToken }) => {
  sdkConfig = { baseUrl, websiteToken };
  try {
    // Initialize error handler
    errorHandler = new ErrorHandler();

    window.dispatchEvent(
      new CustomEvent(WIDGET_EVENTS.INIT, { detail: { baseUrl, websiteToken } })
    );

    const config = await fetchWidgetConfig(baseUrl, websiteToken);
    const chatwootSettings = window.chatwootSettings || config.default_config;

    window.$chatwoot = {
      baseUrl,
      hasLoaded: false,
      hideMessageBubble:
        chatwootSettings.hideMessageBubble || config.hideWidgetIcon || false,
      isOpen: false,
      position: chatwootSettings.position === 'left' ? 'left' : 'right',
      position_mobile:
        chatwootSettings.position_mobile === 'left' ? 'left' : 'right',
      websiteToken,
      locale: chatwootSettings.locale,
      type: getBubbleView(chatwootSettings.type),
      type_mobile: getBubbleView(chatwootSettings.type_mobile),
      launcherTitle: chatwootSettings.launcherTitle || '',
      launcherTitle_mobile: chatwootSettings.launcherTitle_mobile || '',
      bottomMargin: chatwootSettings.bottomMargin || 20,
      leftMargin: chatwootSettings.leftMargin || 20,
      rightMargin: chatwootSettings.rightMargin || 20,
      bottomMargin_mobile: chatwootSettings.bottomMargin_mobile || 20,
      leftMargin_mobile: chatwootSettings.leftMargin_mobile || 20,
      rightMargin_mobile: chatwootSettings.rightMargin_mobile || 20,
      showPopoutButton: chatwootSettings.showPopoutButton || false,
      bubbleImage:
        (isMobile() ? config.widgetIconMobile : config.widgetIconDesktop) ||
        bubbleImg,
      unreadPopup: chatwootSettings.unreadPopup || false,
      isEnabledOnDesktop: chatwootSettings.isEnabledOnDesktop || true,
      isEnabledOnMobile: chatwootSettings.isEnabledOnMobile || true,
      widgetBodyFont: chatwootSettings.widgetBodyFont || 'Lato',
      widgetBGColor: chatwootSettings.widgetBGColor || '#fafafa',
      botAvatarImage:
        chatwootSettings.botAvatarImage ||
        `https://ik.imagekit.io/limechatai/Ellipse_2-3_j9UWmYtQ6.png`,
      widgetZIndex: chatwootSettings.widgetZIndex || WIDGET_Z_INDEX,
      hideWidgetIcon: config.hideWidgetIcon,
      utm_for_widget: config.utm_for_widget,
      disappearOnScrollingDesktop: config.disappearOnScrollingDesktop,
      disappearOnScrollingMobile: config.disappearOnScrollingMobile,

      toggle() {
        IFrameHelper.events.toggleBubble();
      },
      setUser(identifier, user) {
        if (typeof identifier !== 'string' && typeof identifier !== 'number') {
          throw new Error('Identifier should be a string or a number');
        }
        if (!hasUserKeys(user)) {
          throw new Error(
            'User object should have one of the keys [avatar_url, email, name]'
          );
        }
        const userCookieName = getUserCookieName();
        const hashToBeStored = computeHashForUserData({ identifier, user });
        if (hashToBeStored === Cookies.get(userCookieName)) {
          return;
        }
        window.$chatwoot.identifier = identifier;
        window.$chatwoot.user = user;
        IFrameHelper.sendMessage('set-user', { identifier, user });
        Cookies.set(userCookieName, hashToBeStored, {
          expires: 365,
          sameSite: 'Lax',
        });
      },
      setCustomAttributes(customAttributes = {}) {
        if (!customAttributes || !Object.keys(customAttributes).length) {
          throw new Error('Custom attributes should have atleast one key');
        }
        IFrameHelper.sendMessage('set-custom-attributes', {
          customAttributes,
        });
      },
      deleteCustomAttribute(customAttribute = '') {
        if (!customAttribute) {
          throw new Error('Custom attribute is required');
        }
        IFrameHelper.sendMessage('delete-custom-attribute', {
          customAttribute,
        });
      },
      setLabel(label = '') {
        IFrameHelper.sendMessage('set-label', { label });
      },
      removeLabel(label = '') {
        IFrameHelper.sendMessage('remove-label', { label });
      },
      setLocale(localeToBeUsed = 'en') {
        IFrameHelper.sendMessage('set-locale', { locale: localeToBeUsed });
      },
      reset() {
        if (window.$chatwoot.isOpen) {
          IFrameHelper.events.toggleBubble();
        }
        IFrameHelper.removeConversationToken();
        Cookies.remove(getUserCookieName());
        const iframe = IFrameHelper.getAppFrame();
        iframe.src = IFrameHelper.getUrl({
          baseUrl: window.$chatwoot.baseUrl,
          websiteToken: window.$chatwoot.websiteToken,
        });
      },
    };

    IFrameHelper.createFrame({
      baseUrl,
      websiteToken,
      widgetColor: config.widgetColor,
      homepopupsettingOptions: config.homepopupsettingOptions,
      closepopupsettingOptions: config.closepopupsettingOptions,
      popup_configs: config.popup_configs,
      popup_message_enabled: config.popup_message_enabled,
    });

    // Initialize widget handler
    widgetHandler = new WidgetHandler();
    widgetHandler.initialize({
      toggleWidget: () => window.$chatwoot.toggle(),
      loadIframe: loadIframe,
    });

    // Mark widget as created after iframe is set up
    widgetHandler.setWidgetCreated();

    // Listen for widget ready event to mark as loaded
    window.addEventListener(WIDGET_EVENTS.READY, () => {
      if (widgetHandler) {
        widgetHandler.setWidgetLoaded();
      }
    });

    window.dispatchEvent(
      new CustomEvent(WIDGET_EVENTS.WIDGET_CREATED, {
        detail: { baseUrl, websiteToken },
      })
    );
  } catch (error) {
    if (errorHandler) {
      errorHandler.handleError(error, 'run_sdk', { baseUrl, websiteToken });
    } else {
      window.dispatchEvent(
        new CustomEvent(WIDGET_EVENTS.ERROR, {
          detail: { error: error.message, action: 'run_sdk' },
        })
      );
    }
  }
};

const addEventListener = (eventName, callback) => {
  if (typeof callback === 'function') {
    window.addEventListener(eventName, callback);
  }
};

const removeEventListener = (eventName, callback) => {
  if (typeof callback === 'function') {
    window.removeEventListener(eventName, callback);
  }
};

const destroy = () => {
  if (widgetHandler) {
    widgetHandler.destroy();
    widgetHandler = null;
  }
  if (errorHandler) {
    errorHandler.reset();
    errorHandler = null;
  }
  IFrameHelper.removeFrame();
  if (window.$chatwoot) {
    delete window.$chatwoot;
  }
  if (window.chatwootSDK) {
    delete window.chatwootSDK;
  }
};

window.chatwootSDK = {
  run: runSDK,

  toggle() {
    if (widgetHandler) {
      return widgetHandler.handleToggleRequest();
    }
    return false;
  },

  open() {
    if (window.chatwootSDK.isReady()) {
      if (!window.$chatwoot.isOpen) {
        window.$chatwoot.toggle();
      }
      return true;
    }
    return this.toggle();
  },

  close() {
    if (window.chatwootSDK.isReady() && window.$chatwoot.isOpen) {
      window.$chatwoot.toggle();
      return true;
    }
    return false;
  },

  isReady() {
    return !!(widgetHandler && widgetHandler.isReady());
  },

  isLoaded() {
    return !!(window.$chatwoot && window.$chatwoot.hasLoaded);
  },

  isOpen() {
    return !!(window.$chatwoot && window.$chatwoot.isOpen);
  },

  on(eventName, callback) {
    const supportedEvents = [
      'init',
      'widget-created',
      'ready',
      'toggle',
      'error',
    ];
    if (supportedEvents.includes(eventName)) {
      if (eventName === 'ready' && this.isReady()) {
        callback();
      } else {
        addEventListener(`lc_widget:${eventName}`, callback);
      }
    }
  },

  off(eventName, callback) {
    const supportedEvents = [
      'init',
      'widget-created',
      'ready',
      'toggle',
      'error',
    ];
    if (supportedEvents.includes(eventName)) {
      removeEventListener(`lc_widget:${eventName}`, callback);
    }
  },

  getStatus() {
    const baseStatus = {
      isReady: this.isReady(),
      isLoaded: this.isLoaded(),
      isOpen: this.isOpen(),
      hasCustomButtons:
        document.querySelectorAll('.lc-widget-toggler').length > 0,
    };

    if (widgetHandler) {
      return {
        ...baseStatus,
        ...widgetHandler.getWidgetState(),
      };
    }

    return baseStatus;
  },

  initializeCustomButtons() {
    if (widgetHandler) {
      widgetHandler.updateCustomButtonStates();
    } else {
      // eslint-disable-next-line no-console
      console.warn('Widget handler not available yet');
    }
  },

  reset() {
    if (widgetHandler) {
      widgetHandler.reset();
    }
    if (errorHandler) {
      errorHandler.reset();
    }
    destroy();
    runSDK(sdkConfig);
  },
};
